import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus, Trash2, MapPin, Monitor, Blend,
  Settings2, Layers, Gift, Inbox, Scale, HandCoins, Upload, Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Editor } from '@/components/ui/editor'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { MemberAutocomplete } from '@/components/ui/member-autocomplete'
import { CurrencyInput } from '@/components/ui/currency-input'
import { useCommunity } from '@/lib/CommunityContext'
import type {
  Call4PapersCallDraft, CfpCostResponsibility, CfpModality,
  CfpPersonRef, CfpSessionFormat,
} from '@/types/space-wizard'
import type { CallDetail, CallPerson } from '@/types/call4papers'

/**
 * An empty call draft. It lives here, not in whoever uses the form, because
 * os dois consumidores (passo 3 do wizard e o diálogo de criar chamada num
 * space `multi`) precisam exatamente do mesmo ponto de partida.
 */
export const emptyCallDraft = (): Call4PapersCallDraft => ({
  title: '',
  description: '',
  modality: 'in_person',
  submissionOpensDate: '',
  submissionOpensTime: '09:00',
  submissionClosesDate: '',
  submissionClosesTime: '23:59',
  allowedFormats: ['talk', 'workshop'],
  allowedDurations: [30, 60],
  tracks: [],
  eventDates: [],
  bannerFile: null,
  bannerUrl: null,
  featuredFile: null,
  featuredUrl: null,
  owners: [],
  evaluationMode: 'none',
  reviewOpensDate: '',
  reviewOpensTime: '09:00',
  reviewClosesDate: '',
  reviewClosesTime: '23:59',
  minReviewsPerSubmission: 2,
  blindReview: false,
  allowCounterOffer: true,
  airTravelCost: 'none',
  groundTransportCost: 'none',
  parkingCost: 'none',
  accommodationCost: 'none',
  mealCost: 'none',
  speakerFeeMode: 'none',
  speakerFeeAmount: 0,
  speakerBenefits: [],
})

interface Props {
  /** `advanced` habilita a aba de avaliação — em `simple` ninguém dá nota. */
  callType: 'simple' | 'advanced'
  data: Call4PapersCallDraft
  onChange: (data: Call4PapersCallDraft) => void
  /** Busca de perfis da comunidade — a mesma usada em eventos. */
  onSearchMembers?: (search: string) => Promise<CfpPersonRef[]>
  disabled?: boolean
  /**
   * The open tab, when the caller needs to drive it. It serves one case only: a
   * validation error about a field on another tab — without this the message
   * points at something the person cannot see.
   */
  tab?: string
  onTabChange?: (tab: string) => void
}

const FORMATS: CfpSessionFormat[] = [
  'talk', 'lightning_talk', 'keynote', 'workshop', 'tutorial', 'masterclass',
  'panel', 'roundtable', 'case_study', 'demo', 'poster', 'other',
]

const DURATIONS = [15, 20, 30, 45, 60, 90, 120]

const MODALITIES: { value: CfpModality; labelKey: string; icon: typeof MapPin }[] = [
  { value: 'in_person', labelKey: 'modality.in_person', icon: MapPin },
  { value: 'online', labelKey: 'modality.online', icon: Monitor },
  { value: 'hybrid', labelKey: 'modality.hybrid', icon: Blend },
]

const COSTS: { value: CfpCostResponsibility; labelKey: string }[] = [
  { value: 'none', labelKey: 'cost.none' },
  { value: 'organizer', labelKey: 'cost.organizer' },
  { value: 'speaker', labelKey: 'cost.speaker' },
]

const chip = (active: boolean) =>
  cn(
    'rounded-full border px-3 py-1 text-xs transition-colors',
    active
      ? 'border-primary bg-primary-50 text-primary-700 font-medium'
      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
  )

export const CallSetupForm: React.FC<Props> = ({
  callType, data, onChange, onSearchMembers, disabled = false, tab, onTabChange,
}) => {
  const { t } = useTranslation('call4papers')
  const searchProfiles = onSearchMembers ?? (async () => [])
  const { community } = useCommunity()
  const currencyCode = community?.currency || 'BRL'
  const bannerInputRef = React.useRef<HTMLInputElement>(null)
  const featuredInputRef = React.useRef<HTMLInputElement>(null)

  const pickImage = (e: React.ChangeEvent<HTMLInputElement>, kind: 'banner' | 'featured') => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () =>
      onChange({
        ...data,
        [`${kind}File`]: file,
        [`${kind}Url`]: reader.result as string,
      })
    reader.readAsDataURL(file)
  }
  const set = <K extends keyof Call4PapersCallDraft>(key: K, value: Call4PapersCallDraft[K]) =>
    onChange({ ...data, [key]: value })

  /**
   * When submissions close, reviewing opens — a minute later, and closes
   * fifteen days on.
   *
   * It is a suggestion, not a lock: the fields stay editable, and the suggestion
   * is only rewritten while nobody has touched them. The "nobody touched" rule
   * is comparing against what **this function** wrote last time; any other value
   * there is a human choice and is not overwritten.
   *
   * It exists because an advanced call with no review period is a call that does
   * not review — and leaving both fields blank made people find that out only
   * when the queue failed to open.
   */
  const suggested = React.useRef({ opensDate: '', opensTime: '', closesDate: '', closesTime: '' })

  const setSubmissionCloses = (dateKey: string, value: string) => {
    const next = { ...data, [dateKey]: value } as Call4PapersCallDraft
    const endsAt = joinDateTime(next.submissionClosesDate, next.submissionClosesTime)

    const untouched = (k: 'opensDate' | 'opensTime' | 'closesDate' | 'closesTime', atual: string) =>
      atual === '' || atual === suggested.current[k]

    if (
      endsAt &&
      untouched('opensDate', next.reviewOpensDate) &&
      untouched('closesDate', next.reviewClosesDate)
    ) {
      const opensAt = new Date(`${endsAt}`)
      opensAt.setMinutes(opensAt.getMinutes() + 1)
      const closesAt = new Date(opensAt)
      closesAt.setDate(closesAt.getDate() + 15)

      const partsOf = (d: Date) => [
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      ]
      const [oD, oT] = partsOf(opensAt)
      const [cD, cT] = partsOf(closesAt)
      suggested.current = { opensDate: oD, opensTime: oT, closesDate: cD, closesTime: cT }

      onChange({
        ...next,
        reviewOpensDate: oD, reviewOpensTime: oT,
        reviewClosesDate: cD, reviewClosesTime: cT,
      })
      return
    }

    onChange(next)
  }

  /** Deslocamento, hospedagem e alimentação só existem se alguém sai de casa. */
  const hasPhysicalCosts = data.modality !== 'online'

  const changeModality = (modality: CfpModality) =>
    onChange(
      modality === 'online'
        ? // some do formulário: zera para não guardar promessa invisível
          {
            ...data, modality,
            airTravelCost: 'none', groundTransportCost: 'none', parkingCost: 'none',
            accommodationCost: 'none', mealCost: 'none',
          }
        : { ...data, modality }
    )

  const toggleFormat = (value: CfpSessionFormat) =>
    set('allowedFormats', data.allowedFormats.includes(value)
      ? data.allowedFormats.filter((f) => f !== value)
      : [...data.allowedFormats, value])

  const toggleDuration = (value: number) =>
    set('allowedDurations', data.allowedDurations.includes(value)
      ? data.allowedDurations.filter((d) => d !== value)
      : [...data.allowedDurations, value].sort((a, b) => a - b))

  const addEventDate = () =>
    set('eventDates', [
      ...data.eventDates,
      { key: `d${Date.now()}`, date: '', startTime: '09:00', endTime: '18:00' },
    ])
  const updateEventDate = (key: string, field: 'date' | 'startTime' | 'endTime', value: string) =>
    set('eventDates', data.eventDates.map((d) => (d.key === key ? { ...d, [field]: value } : d)))
  const removeEventDate = (key: string) =>
    set('eventDates', data.eventDates.filter((d) => d.key !== key))

  const addTrack = () =>
    set('tracks', [...data.tracks, { key: `t${Date.now()}`, name: '', purpose: '', reviewers: [] }])
  const updateTrack = (key: string, field: 'name' | 'purpose', value: string) =>
    set('tracks', data.tracks.map((t) => (t.key === key ? { ...t, [field]: value } : t)))
  const setTrackReviewers = (key: string, reviewers: CfpPersonRef[]) =>
    set('tracks', data.tracks.map((t) => (t.key === key ? { ...t, reviewers } : t)))
  const removeTrack = (key: string) =>
    set('tracks', data.tracks.filter((t) => t.key !== key))

  const benefitRows = [...data.speakerBenefits, '']
  const changeBenefit = (index: number, value: string) => {
    const next = [...benefitRows]
    next[index] = value
    set('speakerBenefits', next.filter((b) => b.trim() !== ''))
  }
  const removeBenefit = (index: number) =>
    set('speakerBenefits', data.speakerBenefits.filter((_, i) => i !== index))

  /** Par data + hora, no mesmo formato do formulário de evento. */
  const dateTimeField = (
    label: string,
    dateKey: keyof Call4PapersCallDraft,
    timeKey: keyof Call4PapersCallDraft,
    /** Who writes the value. Only the submission close needs one: it drags the
        review window along. */
    write: (key: string, value: string) => void = (k, v) => set(k as never, v as never),
    hint?: string,
  ) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {/* Grid 50/50: dá folga suficiente para o ícone nativo dos dois seletores.
          min-w-0 evita que o conteúdo intrínseco do input estoure a coluna. */}
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="date"
          className="w-full min-w-0"
          value={String(data[dateKey] ?? '')}
          onChange={(e) => write(dateKey as string, e.target.value)}
          disabled={disabled}
        />
        <Input
          type="time"
          className="w-full min-w-0"
          value={String(data[timeKey] ?? '')}
          onChange={(e) => write(timeKey as string, e.target.value)}
          disabled={disabled}
        />
      </div>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  )

  const counter = (n: number, active = false) =>
    n > 0 ? (
      <span className={cn(
        'rounded-full px-1.5 text-[10px] font-semibold',
        active ? 'bg-white/20' : 'bg-black/10'
      )}>{n}</span>
    ) : null

  type CostField =
    | 'airTravelCost' | 'groundTransportCost' | 'parkingCost'
    | 'accommodationCost' | 'mealCost'

  const costRow = (label: string, field: CostField) => (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
      <span className="min-w-0 text-sm text-gray-700">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {COSTS.map(({ value, labelKey }) => (
          <button key={value} type="button" disabled={disabled}
            onClick={() => set(field, value)} className={chip(data[field] === value)}>
            {t(labelKey)}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <Tabs
      /* Controlled only when the caller wants it. Without `value`, Radix keeps
         its own state and the tab does not jump around while typing. */
      {...(tab ? { value: tab, onValueChange: onTabChange } : { defaultValue: 'geral' })}
      className="w-full"
    >
      <TabsList className="mb-6">
        <TabsTrigger value="geral">
          <Settings2 className="h-4 w-4 shrink-0" />{t('setup.tabs.general')}
        </TabsTrigger>
        <TabsTrigger value="trilhas">
          <Layers className="h-4 w-4 shrink-0" />{t('setup.tabs.tracks')}{counter(data.tracks.length)}
        </TabsTrigger>
        <TabsTrigger value="beneficios">
          <Gift className="h-4 w-4 shrink-0" />{t('setup.tabs.benefits')}{counter(data.speakerBenefits.length)}
        </TabsTrigger>
        <TabsTrigger value="submissoes">
          <Inbox className="h-4 w-4 shrink-0" />{t('setup.tabs.submissions')}
        </TabsTrigger>
        {callType === 'advanced' && (
          <TabsTrigger value="avaliacao">
            <Scale className="h-4 w-4 shrink-0" />{t('setup.tabs.review')}
          </TabsTrigger>
        )}
        <TabsTrigger value="remuneracao">
          <HandCoins className="h-4 w-4 shrink-0" />
          <span className="sm:hidden">{hasPhysicalCosts ? t('setup.tabs.costsShort') : t('setup.tabs.fee')}</span>
          <span className="hidden sm:inline">{hasPhysicalCosts ? t('setup.tabs.costs') : t('setup.tabs.fee')}</span>
        </TabsTrigger>
      </TabsList>

      {/* ---------- Geral ---------- */}
      <TabsContent value="geral" className="space-y-5">
        {/* Duas colunas como no CreateEventDialog: o campo rico de um lado,
            datas, imagens e pessoas do outro. Esticado numa coluna só,
            um Input ocupava mil pixels de largura. */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-5 lg:border-r lg:pr-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('setup.general.name')}</label>
              <Input value={data.title} disabled={disabled}
                onChange={(e) => set('title', e.target.value)}
                placeholder={t('setup.general.namePlaceholder')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('setup.general.description')}</label>
              <Editor aiFieldKind="call" content={data.description} onChange={(html: string) => set('description', html)}
                placeholder={t('setup.general.descriptionPlaceholder')}
                className="min-h-[140px]" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('setup.general.images')}</label>
              {/* Mesmo layout do CreateEventDialog: dois cards com preview de h-24. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <div className="mb-2 text-sm font-medium">{t('setup.general.coverImage')}</div>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => pickImage(e, 'banner')}
                  />
                  {data.bannerUrl ? (
                    <div className="group relative h-24 w-full overflow-hidden rounded-lg">
                      <img src={data.bannerUrl} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => bannerInputRef.current?.click()}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Upload className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-24 w-full"
                      disabled={disabled}
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t('setup.general.upload')}
                    </Button>
                  )}
                  <div className="mt-2 text-xs text-gray-500">{t('setup.general.coverHint')}</div>
                </div>

                <div className="rounded-lg border p-4">
                  <div className="mb-2 text-sm font-medium">{t('setup.general.featuredImage')}</div>
                  <input
                    ref={featuredInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => pickImage(e, 'featured')}
                  />
                  {data.featuredUrl ? (
                    <div className="group relative h-24 w-full overflow-hidden rounded-lg">
                      <img src={data.featuredUrl} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => featuredInputRef.current?.click()}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Upload className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-24 w-full"
                      disabled={disabled}
                      onClick={() => featuredInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t('setup.general.upload')}
                    </Button>
                  )}
                  <div className="mt-2 text-xs text-gray-500">{t('setup.general.featuredHint')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('setup.general.modality')}</label>
              <div className="flex flex-wrap gap-2">
                {MODALITIES.map(({ value, labelKey, icon: Icon }) => (
                  <button key={value} type="button" disabled={disabled}
                    onClick={() => changeModality(value)}
                    className={cn(chip(data.modality === value), 'inline-flex items-center gap-1.5')}>
                    <Icon className="h-3.5 w-3.5" />{t(labelKey)}
                  </button>
                ))}
              </div>
              {data.modality === 'hybrid' && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                  {t('setup.general.hybridHint')}
                </p>
              )}
              {data.modality === 'online' && (
                <p className="text-xs text-gray-500">
                  {t('setup.general.onlineHint')}
                </p>
              )}
            </div>


            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm font-medium text-gray-700">{t('setup.general.whenHappens')}</label>
                <button type="button" onClick={addEventDate} disabled={disabled}
                  className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" />
                  {t('setup.general.addDate')}
                </button>
              </div>

              {data.eventDates.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-500">
                  {t('setup.general.noDates')}
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Nada de largura fixa: o ícone nativo de relógio/calendário
                      não cabia e ficava cortado. Os campos crescem no espaço que
                      sobra, e a lixeira encosta neles em vez de ir para o canto. */}
                  {data.eventDates.map((d) => (
                    <div key={d.key} className="flex flex-wrap items-center gap-2">
                      <Input type="date" className="min-w-[138px] flex-1" value={d.date} disabled={disabled}
                        onChange={(e) => updateEventDate(d.key, 'date', e.target.value)} />
                      <div className="flex min-w-[220px] flex-[2] items-center gap-2">
                        <Input type="time" className="min-w-0 flex-1" value={d.startTime} disabled={disabled}
                          onChange={(e) => updateEventDate(d.key, 'startTime', e.target.value)} />
                        <span className="flex-shrink-0 text-xs text-gray-400">{t('setup.general.timeTo')}</span>
                        <Input type="time" className="min-w-0 flex-1" value={d.endTime} disabled={disabled}
                          onChange={(e) => updateEventDate(d.key, 'endTime', e.target.value)} />
                        <button type="button" onClick={() => removeEventDate(d.key)} disabled={disabled}
                          className="flex-shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          aria-label={t('setup.general.removeDate')}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">
                {t('setup.general.datesHint')}
              </p>
            </div>


            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('setup.general.owners')}</label>
              <MemberAutocomplete
                selectedMembers={data.owners}
                onMembersChange={(owners) => set('owners', owners as CfpPersonRef[])}
                onSearch={searchProfiles}
                placeholder={t('setup.general.searchMembers')}
                disabled={disabled}
              />
              <p className="text-xs text-gray-500">
                {t('setup.general.ownersHint')}
              </p>
            </div>

            {callType === 'advanced' ? (
              /* A reviewer belongs to the track, not the call — asking here would
                 force a second screen just to say who reviews what. */
              <p
                className="rounded-md bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500"
                dangerouslySetInnerHTML={{ __html: t('setup.general.reviewersInTracks') }}
              />
            ) : (
              <p
                className="rounded-md bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500"
                dangerouslySetInnerHTML={{ __html: t('setup.general.simpleNoReviewers') }}
              />
            )}
          </div>
        </div>
      </TabsContent>

      {/* ---------- Trilhas ---------- */}
      <TabsContent value="trilhas" className="space-y-3">
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">
            {t('setup.tracks.hint')}
          </p>
          <button type="button" onClick={addTrack} disabled={disabled}
            className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline">
            <Plus className="h-3.5 w-3.5" />{t('setup.tracks.add')}
          </button>
        </div>
        {data.tracks.length === 0 ? (
          <p className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-500">
            {t('setup.tracks.empty')}
          </p>
        ) : (
          <div className="space-y-2">
            {data.tracks.map((track) => (
              <div key={track.key} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input value={track.name} disabled={disabled}
                      onChange={(e) => updateTrack(track.key, 'name', e.target.value)}
                      placeholder={t('setup.tracks.namePlaceholder')} />
                    <Textarea aiFieldKind="call" rows={2} value={track.purpose} disabled={disabled}
                      onChange={(e) => updateTrack(track.key, 'purpose', e.target.value)}
                      placeholder={t('setup.tracks.purposePlaceholder')} />

                    {/* Quem avalia mora na própria trilha: é onde a decisão é
                        tomada e o único lugar onde ela faz sentido — a mesma
                        pessoa pode avaliar uma trilha e não a outra.
                        Não há aceite: quem é posto aqui já avalia. */}
                    {callType === 'advanced' && (
                      <div className="space-y-1.5 border-t border-gray-100 pt-2">
                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <Users className="h-3.5 w-3.5 text-gray-400" />
                          {t('setup.tracks.reviewers')}
                        </label>
                        <MemberAutocomplete
                          selectedMembers={track.reviewers}
                          onMembersChange={(reviewers) =>
                            setTrackReviewers(track.key, reviewers as CfpPersonRef[])}
                          onSearch={searchProfiles}
                          placeholder={t('setup.general.searchMembers')}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => removeTrack(track.key)} disabled={disabled}
                    className="mt-1 rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    aria-label={t('setup.tracks.remove')}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      {/* ---------- Benefícios ---------- */}
      <TabsContent value="beneficios" className="space-y-3">
        <p className="text-xs text-gray-500">
          {t('setup.benefits.hint')}
        </p>
        <div className="space-y-2">
          {benefitRows.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input value={benefit} disabled={disabled}
                onChange={(e) => changeBenefit(index, e.target.value)}
                placeholder={t('setup.benefits.placeholder')} />
              {benefit && (
                <button type="button" onClick={() => removeBenefit(index)} disabled={disabled}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                  aria-label={t('setup.benefits.remove')}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </TabsContent>

      {/* ---------- Submissões ---------- */}
      <TabsContent value="submissoes" className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {dateTimeField(t('setup.submissions.opens'), 'submissionOpensDate', 'submissionOpensTime')}
          {dateTimeField(
            t('setup.submissions.closes'), 'submissionClosesDate', 'submissionClosesTime',
            setSubmissionCloses,
            callType === 'advanced' ? t('setup.submissions.closesHint') : undefined,
          )}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">{t('setup.submissions.formats')}</label>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((value) => (
              <button key={value} type="button" disabled={disabled}
                onClick={() => toggleFormat(value)} className={chip(data.allowedFormats.includes(value))}>
                {String(t(`format.${value}`, { defaultValue: value }))}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">{t('setup.submissions.durations')}</label>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((minutes) => (
              <button key={minutes} type="button" disabled={disabled}
                onClick={() => toggleDuration(minutes)} className={chip(data.allowedDurations.includes(minutes))}>
                {t('minutesShort', { count: minutes })}
              </button>
            ))}
          </div>
        </div>
      </TabsContent>

      {/* ---------- Avaliação (só avançada) ---------- */}
      {callType === 'advanced' && (
        <TabsContent value="avaliacao" className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t('setup.review.who')}</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'committee' as const, labelKey: 'setup.review.modeCommittee' },
                { value: 'community' as const, labelKey: 'setup.review.modeCommunity' },
                { value: 'none' as const, labelKey: 'setup.review.modeNone' },
              ].map(({ value, labelKey }) => (
                <button key={value} type="button" disabled={disabled}
                  onClick={() => set('evaluationMode', value)} className={chip(data.evaluationMode === value)}>
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* O período vale para os dois modos: com comitê é quando se dá nota,
              com a comunidade é quando se vota. É a mesma janela — pedir duas
              datas para a mesma coisa é o que faz uma delas ficar vazia. */}
          {data.evaluationMode !== 'none' && (
            <div className="grid gap-4 sm:grid-cols-2">
              {dateTimeField(t('setup.review.opens'), 'reviewOpensDate', 'reviewOpensTime')}
              {dateTimeField(t('setup.review.closes'), 'reviewClosesDate', 'reviewClosesTime')}
            </div>
          )}

          {data.evaluationMode === 'committee' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('setup.review.minReviews')}</label>
                <Input type="number" min={1} max={10} className="max-w-[120px]" disabled={disabled}
                  value={data.minReviewsPerSubmission}
                  onChange={(e) => set('minReviewsPerSubmission', Number(e.target.value) || 1)} />
              </div>
              <label className="flex cursor-pointer items-start gap-2">
                <input type="checkbox" className="mt-0.5" checked={data.blindReview} disabled={disabled}
                  onChange={(e) => set('blindReview', e.target.checked)} />
                <span className="text-sm text-gray-700">{t('setup.review.blind')}
                  <span className="block text-xs text-gray-500">
                    {t('setup.review.blindHint')}
                  </span>
                </span>
              </label>
            </>
          )}

          <label className="flex cursor-pointer items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={data.allowCounterOffer} disabled={disabled}
              onChange={(e) => set('allowCounterOffer', e.target.checked)} />
            <span className="text-sm text-gray-700">{t('setup.review.counterOffer')}
              <span className="block text-xs text-gray-500">
                {t('setup.review.counterOfferHint')}
              </span>
            </span>
          </label>
        </TabsContent>
      )}

      {/* ---------- Custos e cachê ---------- */}
      <TabsContent value="remuneracao" className="space-y-5">
        {hasPhysicalCosts && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t('setup.costs.title')}</label>
            <div className="space-y-2">
              {costRow(t('setup.costs.airTravel'), 'airTravelCost')}
              {costRow(t('setup.costs.groundTransport'), 'groundTransportCost')}
              {costRow(t('setup.costs.parking'), 'parkingCost')}
              {costRow(t('setup.costs.accommodation'), 'accommodationCost')}
              {costRow(t('setup.costs.meal'), 'mealCost')}
            </div>
            {data.modality === 'hybrid' && (
              <p className="text-xs text-gray-500">
                {t('setup.costs.hybridHint')}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">{t('setup.fee.title')}</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'none' as const, labelKey: 'setup.fee.none' },
              { value: 'fixed' as const, labelKey: 'setup.fee.fixed' },
              { value: 'speaker_defined' as const, labelKey: 'setup.fee.speakerDefined' },
            ].map(({ value, labelKey }) => (
              <button key={value} type="button" disabled={disabled}
                onClick={() => set('speakerFeeMode', value)} className={chip(data.speakerFeeMode === value)}>
                {t(labelKey)}
              </button>
            ))}
          </div>
          {data.speakerFeeMode === 'fixed' && (
            <div className="max-w-[200px] space-y-1">
              {community?.currencyFormat ? (
                <CurrencyInput
                  value={data.speakerFeeAmount}
                  onChange={(value) => set('speakerFeeAmount', value)}
                  currencyFormat={community.currencyFormat}
                  disabled={disabled}
                  min={0}
                />
              ) : (
                <div className="relative">
                  <Input type="number" min="0" step="0.01" className="pr-16" disabled={disabled}
                    value={data.speakerFeeAmount}
                    onChange={(e) => set('speakerFeeAmount', parseFloat(e.target.value) || 0)} />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 rounded bg-gray-50 px-2 py-1 text-sm font-medium text-gray-500">
                    {currencyCode}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500">{t('setup.fee.currencyHint', { currency: currencyCode })}</p>
            </div>
          )}
          {data.speakerFeeMode === 'speaker_defined' && (
            <p className="text-xs text-gray-500">
              {t('setup.fee.speakerDefinedHint')}
            </p>
          )}
          {callType === 'simple' && (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500">
              {t('setup.fee.simpleHint')}
            </p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  )
}

/* ---------------------------------------------------------------------------
 * Conversão entre a chamada salva e o rascunho do formulário
 * ------------------------------------------------------------------------ */

/** `2027-11-12T09:00:00.000` -> `['2027-11-12', '09:00']`. No timezone, house style. */
const splitDateTime = (iso?: string | null): [string, string] => {
  if (!iso) return ['', '']
  const [date, rest = ''] = iso.split('T')
  return [date, rest.slice(0, 5)]
}

/**
 * What stops a call from being saved. Returns the error's i18n key, or `null`.
 *
 * It lives here, next to the fields, and not in the dialog, because the two
 * paths that save a call — the listing's and the detail page's — use the same
 * form. Validating in only one would let the other through.
 *
 * A simple call escapes almost all of it on purpose: it is "one question instead
 * of three". An advanced one does not: with no submission deadline it never
 * opens, and with no review window the queue never starts — and people only
 * found that out afterwards.
 */
export const validateCallDraft = (
  draft: Call4PapersCallDraft,
  callType: 'simple' | 'advanced',
): string | null => {
  if (!draft.title.trim()) return 'createDialog.errors.titleRequired'

  const submissionOpens = joinDateTime(draft.submissionOpensDate, draft.submissionOpensTime)
  const submissionCloses = joinDateTime(draft.submissionClosesDate, draft.submissionClosesTime)

  if (callType === 'advanced') {
    if (!submissionOpens || !submissionCloses) return 'createDialog.errors.submissionPeriod'
  }
  if (submissionOpens && submissionCloses && submissionCloses <= submissionOpens) {
    return 'createDialog.errors.submissionOrder'
  }

  if (callType === 'advanced' && draft.evaluationMode !== 'none') {
    const reviewOpens = joinDateTime(draft.reviewOpensDate, draft.reviewOpensTime)
    const reviewCloses = joinDateTime(draft.reviewClosesDate, draft.reviewClosesTime)
    if (!reviewOpens || !reviewCloses) return 'createDialog.errors.reviewPeriod'
    if (reviewCloses <= reviewOpens) return 'createDialog.errors.reviewOrder'
    /* Reviewing before submissions close is reading half a call — and it makes
       the derived status flip between open and reviewing. */
    if (submissionCloses && reviewOpens < submissionCloses) {
      return 'createDialog.errors.reviewBeforeSubmission'
    }
  }

  return null
}

const joinDateTime = (date: string, time: string) =>
  date ? `${date}T${time || '00:00'}:00.000` : null

const toPersonRef = (p: CallPerson): CfpPersonRef => ({
  id: p.id,
  name: p.name,
  role: p.role ?? '',
  avatar: p.avatarUrl ?? undefined,
  type: 'profile',
})

/**
 * Opens an existing call in the form.
 *
 * The form keeps date and time apart, the call keeps the whole datetime — the
 * conversion at both ends lives here so it does not leak into the screens.
 */
export const callDraftFromDetail = (call: CallDetail): Call4PapersCallDraft => {
  const [submissionOpensDate, submissionOpensTime] = splitDateTime(call.submissionOpensAt)
  const [submissionClosesDate, submissionClosesTime] = splitDateTime(call.submissionClosesAt)
  /* Um campo na tela, duas colunas no banco: com a comunidade a janela é a do
     voto. Ler só `reviewOpensAt` abriria o formulário em branco num edital que
     tem o período preenchido — e salvar de volta o apagaria. */
  const votes = call.evaluationMode === 'community'
  const [reviewOpensDate, reviewOpensTime] =
    splitDateTime(votes ? call.votingOpensAt ?? call.reviewOpensAt : call.reviewOpensAt)
  const [reviewClosesDate, reviewClosesTime] =
    splitDateTime(votes ? call.votingClosesAt ?? call.reviewClosesAt : call.reviewClosesAt)

  return {
    ...emptyCallDraft(),
    title: call.title,
    description: call.description,
    modality: call.modality,
    bannerUrl: call.bannerUrl ?? null,
    featuredUrl: call.featuredUrl ?? null,
    owners: call.owners.map(toPersonRef),
    submissionOpensDate, submissionOpensTime,
    submissionClosesDate, submissionClosesTime,
    reviewOpensDate, reviewOpensTime,
    reviewClosesDate, reviewClosesTime,
    allowedFormats: call.allowedFormats as CfpSessionFormat[],
    allowedDurations: call.allowedDurations,
    /* Each track carries its own committee. The link arrives flat in
       `trackReviewers`, and this is where it folds back into the track — which
       is where the form edits it. */
    tracks: call.tracks.map((t) => ({
      key: t.id,
      name: t.name,
      purpose: t.purpose,
      reviewers: (call.trackReviewers ?? [])
        .filter((link) => link.trackId === t.id)
        .map((link) => toPersonRef(link.member)),
    })),
    eventDates: call.eventDates.map((d) => ({ key: `${d.date}-${d.startTime}`, ...d })),
    evaluationMode: call.evaluationMode,
    minReviewsPerSubmission: call.minReviewsPerSubmission ?? 2,
    blindReview: call.blindReview ?? false,
    allowCounterOffer: call.allowCounterOffer ?? false,
    airTravelCost: call.airTravelCost,
    groundTransportCost: call.groundTransportCost,
    parkingCost: call.parkingCost,
    accommodationCost: call.accommodationCost,
    mealCost: call.mealCost,
    speakerFeeMode: call.speakerFeeMode,
    speakerFeeAmount: call.speakerFeeAmount ?? 0,
    speakerBenefits: call.speakerBenefits,
  }
}

/** Turns what was edited back into the shape the page displays. */
export const applyDraftToDetail = (
  call: CallDetail,
  draft: Call4PapersCallDraft,
): CallDetail => ({
  ...call,
  title: draft.title,
  description: draft.description,
  modality: draft.modality,
  bannerUrl: draft.bannerUrl ?? null,
  featuredUrl: draft.featuredUrl ?? null,
  owners: draft.owners.map((p) => ({ id: p.id, name: p.name, role: p.role, avatarUrl: p.avatar ?? null })),
  reviewers: draft.tracks
    .flatMap((t) => t.reviewers)
    .filter((p, i, all) => all.findIndex((o) => o.id === p.id) === i)
    .map((p) => ({ id: p.id, name: p.name, role: p.role, avatarUrl: p.avatar ?? null })),
  submissionOpensAt: joinDateTime(draft.submissionOpensDate, draft.submissionOpensTime),
  submissionClosesAt: joinDateTime(draft.submissionClosesDate, draft.submissionClosesTime),
  reviewOpensAt: joinDateTime(draft.reviewOpensDate, draft.reviewOpensTime),
  reviewClosesAt: joinDateTime(draft.reviewClosesDate, draft.reviewClosesTime),
  votingOpensAt: draft.evaluationMode === 'community'
    ? joinDateTime(draft.reviewOpensDate, draft.reviewOpensTime)
    : null,
  votingClosesAt: draft.evaluationMode === 'community'
    ? joinDateTime(draft.reviewClosesDate, draft.reviewClosesTime)
    : null,
  allowedFormats: draft.allowedFormats,
  allowedDurations: draft.allowedDurations,
  /* A new track has no server id yet; the local key stands in until the API
     answers. The proposal count belongs to the server, so it is preserved. */
  tracks: draft.tracks.map((t) => ({
    id: t.key,
    name: t.name,
    purpose: t.purpose,
    submissionCount: call.tracks.find((o) => o.id === t.key)?.submissionCount,
  })),
  trackCount: draft.tracks.length,
  eventDates: draft.eventDates.map(({ date, startTime, endTime }) => ({ date, startTime, endTime })),
  evaluationMode: draft.evaluationMode,
  minReviewsPerSubmission: draft.minReviewsPerSubmission,
  blindReview: draft.blindReview,
  allowCounterOffer: draft.allowCounterOffer,
  airTravelCost: draft.airTravelCost,
  groundTransportCost: draft.groundTransportCost,
  parkingCost: draft.parkingCost,
  accommodationCost: draft.accommodationCost,
  mealCost: draft.mealCost,
  speakerFeeMode: draft.speakerFeeMode,
  speakerFeeAmount: draft.speakerFeeAmount,
  speakerBenefits: draft.speakerBenefits,
})
